export type QixitAuthContext = 'mailbox' | 'paymentfast' | 'purchasefast';

export type QixitAuthSuccess = {
  kind: 'success';
  token: string;
  userId: string;
  username: string;
  refreshToken?: string;
  /** Host-specific login payload (mailbox keeps account-type flags here). */
  raw?: unknown;
};

export type QixitAuthMfaChallenge = {
  kind: 'mfa';
  userId: string;
  mfaSessionToken: string;
  preferredMethod?: string;
  availableMethods: string[];
  availablePasskeys: Array<{ passkeyId: string; label: string; transports?: string }>;
  mfaScopeCredentialId?: string | null;
};

export type QixitAuthIpChallenge = {
  kind: 'ip';
  ipVerificationToken: string;
  maskedEmail?: string;
};

export type QixitAuthResult = QixitAuthSuccess | QixitAuthMfaChallenge | QixitAuthIpChallenge;

export type QixitAuthMethod = 'password' | 'mfa' | 'social' | 'express';

export type QixitAuthMeta = {
  method: QixitAuthMethod;
  /** Set when the session was just created by express signup. */
  isNewAccount?: boolean;
  isProvisional?: boolean;
  mfaScopeCredentialId?: string | null;
};

export type QixitAuthAttribution = {
  referralCode?: string;
  sourcePage?: string;
  payoutId?: string;
  productId?: string;
};

/**
 * MailBox.API calls, supplied by each host so the panel works with both the
 * same-origin mailbox client and the cross-origin checkout client.
 */
export interface QixitAuthApi {
  /** Base URL of MailBox.API; '' when the host proxies /api on its own origin. */
  apiBase: string;
  get<T>(path: string, params?: Record<string, string>): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  login(username: string, password: string): Promise<QixitAuthResult>;
  completeMfa(request: {
    token: string;
    method: string;
    code?: string;
    assertionResponse?: unknown;
    availableMethods: string[];
  }): Promise<QixitAuthResult>;
  verifyIp(request: { token: string; code: string }): Promise<QixitAuthResult>;
  requestMfaEmail(token: string): Promise<{ maskedEmail: string }>;
  getPasskeyAuthOptions(args: {
    userId: string;
    passkeyId?: string;
    preferPlatform?: boolean;
    preferHybrid?: boolean;
    scopeCredentialId?: string | null;
  }): Promise<unknown>;
  getDeviceId(): string;
  /** Trusted-device fingerprint (same value password login sends as X-Fingerprint). */
  getFingerprint?(): string;
}

export function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string; Message?: string; error?: string } }; message?: string } | null;
  return (
    e?.response?.data?.message ||
    e?.response?.data?.Message ||
    e?.response?.data?.error ||
    (err instanceof Error ? err.message : '') ||
    fallback
  );
}
